import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ConfigService } from '@xilo-mono/common/config';
import { JsonbType, type PolicyProductLine, type RateJob, RateRequest } from '@xilo-mono/form-contracts';
import { concatMap, delay, iif, map, type Observable, of, retryWhen, switchMap, throwError } from 'rxjs';

const isLocalDevMode = (window.location.host === 'localhost:4200');
const localDevModeApiUrl = 'http://localhost:3333/api';
const localDevModeClientId = 19;

@Injectable({ providedIn: 'root' })
export class RatesService {
  private readonly http = inject(HttpClient);
  private readonly configService = inject(ConfigService);
  private readonly workflowApiUrl = isLocalDevMode ? localDevModeApiUrl : `${this.configService.workflowsApiUrl}`;

  getRateJobs(
    token: string,
    clientId: string,
    orgId: number,
    submissionId?: string,
    formViewKey?: string,
  ): Observable<RateJob[]> {
    const lob: PolicyProductLine = 'auto'; // TODO: figure out how to remove hardcoded values
    const carrierIds: number[] = [1, 17, 36] // TODO: figure out how to remove hardcoded values, currently Progressive, The General and Travelers
    const clientIdInteger = parseInt(clientId, 10);
    // Poll for a max of a minute for the insuranceData
    return this.getClientInsuranceData(token, orgId, clientIdInteger).pipe(
      switchMap((res: JsonbType) => {
        const insuranceData = res.find(id =>
          id.productLine === 'auto' || id.productLine === 'personal_package'
        );

        const request: RateRequest = {
          orgId: orgId,
          insuranceDataId: insuranceData?.id,
          execMode: 'test',
          clientId: clientIdInteger,
          lob,
          carrierIds,
          data: insuranceData?.data,
          usState: insuranceData?.data?.RatingState,
          status: 'Created',
          createdById: clientIdInteger,
          createdByRole: 'admin',
          submissionId: +submissionId,
          formViewKey,
        };
        // Create rate request and poll for jobs
        return this.createRateRequest(token, request).pipe(
          switchMap((rateReq) => {
            return this.pollRateRequestJobs(token, orgId, rateReq.id).pipe(
              map((rateResp) => rateResp.jobs)
            );
          })
        );
      })
    );
  }

  /**
   * Methods below are copied from the WorkflowApiService and IntegrationMappingService,
   * because these services can not be injected here (some dependencies are missing).
   */

  /**
   * Copy of WorkflowApiService.getClientInsuranceData()
   */
  private getClientInsuranceData(token:string, orgId: number, clientId: number) {
    return this.http.get(`${this.workflowApiUrl}/org/${orgId}/insurance-data/client/${clientId}`, {
      headers: {
        'x-access-token': token
      }
    });
  }

  /**
   * Copy of IntegrationMappingService.createRateRequest()
   */
  private createRateRequest(token:string, body: RateRequest): Observable<RateRequest> {
    return this.http.post<RateRequest>(`${this.workflowApiUrl}/org/${body.orgId}/rate-requests`, body, {
      headers: {
        'x-access-token': token
      }
    });
  }

  /**
   * Copy of IntegrationMappingService.getRateRequestJobs()
   */
  private getRateRequestJobs(token:string, companyId: number, requestId: number): Observable<any> {
    return this.http.get(`${this.workflowApiUrl}/org/${companyId}/rate-requests/${requestId}/jobs`, {
      headers: {
        'x-access-token': token
      }
    });
  }

  /**
   * Copy of IntegrationMappingService.pollRateRequestJobs()
   */
  private pollRateRequestJobs(token:string, companyId: number, requestId: number) {
    return this.getRateRequestJobs(token, companyId, requestId).pipe(
      map((res: RateRequest) => {
        if (res.status === 'Failed' || res.status === 'Done' || res.status === 'Cancelled') return res
        throw new Error('pending');
      }),
      retryWhen((errors) =>
        errors.pipe(
          concatMap((e, i) => {
            // Call every 5 seconds with a timeout of 5 minutes
            return iif(() => i > 60 || e?.message?.includes('failed'), throwError(e), of(e).pipe(delay(5000)))
          })
        )
      ),
    )
  }

  private pollInsuranceData(token:string, companyId: number, clientId: number) {
    return this.getClientInsuranceData(token, companyId, clientId).pipe(
      map((res: JsonbType) => {
        const insuranceData = res.find(id =>
          id.productLine === 'auto' || id.productLine === 'personal_package'
        );
        if (insuranceData) return insuranceData;
        throw new Error('pending');
      }),
      retryWhen((errors) =>
        errors.pipe(
          concatMap((e, i) => {
            // Call every 1 seconds with a timeout of 1 minute
            return iif(() => i > 60 || e?.message?.includes('failed'), throwError(e), of(e).pipe(delay(1000)))
          })
        )
      ),
    )
  }
}